import { CallSessionHelper } from "@/common/helpers";
import { VoiceDetailsCard } from "@/components/atoms/interactionDetailsCard";
import { VoiceInteractionProps } from "@/components/atoms/interactionDetailsCard/types";
import { useState, useEffect } from "react";
import { AgentInteractionStatus } from "@/common/constants/callSession";
import {
	CallSessionState,
	useCallSessionStore,
} from "@/common/stores/nonpersistingStores/callSession";
import { AcsClientType, PlatformType } from "@/common/constants/shared";
import { useAppSettingsStore } from "@/common/stores/persistingStores/app/appSettingsStore";
import { AppSettingsState } from "@/common/stores/persistingStores/app";
import { embededInTeams } from "@/common/authentication/authenticationUtils";

const hiddenStatus: AgentInteractionStatus[] = [
	AgentInteractionStatus.Terminated,
	AgentInteractionStatus.Active,
	AgentInteractionStatus.AcceptedWarmTransferInvitation,
	AgentInteractionStatus.CallbackRegistered,
	AgentInteractionStatus.OutboundAcceptedByAgent,
];

const visibleStatus: AgentInteractionStatus[] = [
	AgentInteractionStatus.Offering,
	AgentInteractionStatus.ReceivingWarmTransferInvitation,
];

// A simultaneous offer that ended for this agent leaves a state in neither list above, which would strand the
// card on screen. These only ever occur for simultaneous offering, so listing them affects no other flow.
const simultaneousOfferingEndedStatus: AgentInteractionStatus[] = [
	AgentInteractionStatus.SimultaneousOfferingTerminated,
	AgentInteractionStatus.SimultaneousOfferingCallerLeft,
	AgentInteractionStatus.SimultaneousOfferingIgnored,
];

export const VoiceInteractionCard = () => {
	const [details, setDetails] = useState<VoiceInteractionProps>();

	const callSession = useCallSessionStore(
		(state: CallSessionState) => state.callSession
	);

	const appSettings = useAppSettingsStore(
		(state: AppSettingsState) => state.settings
	);

	useEffect(() => {
		const callState = callSession?.state;

		// With the MS Teams toaster offering method the call rings natively in Teams and the in-client
		// simultaneous card is suppressed, so this is the only place the agent sees who is calling and from
		// which queue. The ACS softphone check below does not apply to it: the invite targets the agent's
		// Teams identity, so Teams rings regardless of the tenant's ACS client type.
		const isMsTeamsToasterOffer = !!callSession?.isMsTeamsToasterOffering;

		if (
			!embededInTeams ||
			callState == undefined ||
			hiddenStatus.includes(callState) ||
			simultaneousOfferingEndedStatus.includes(callState) ||
			callSession?.isSimultaneousOffering ||
			(!isMsTeamsToasterOffer &&
				callSession?.platformType !== PlatformType.Graph &&
				appSettings.acsClientType === AcsClientType.CCaaS)
		) {
			setDetails(undefined);

			return;
		}

		// isMsTeamsToasterOffer needs no state allowlist of its own: the server sets it from the same
		// "not a participant yet, still in SimultaneousOfferingAgents" predicate that resolves the state to
		// SimultaneousOffering (CallSessionObjectV2Extensions), so it is only ever true in that state.
		if (visibleStatus.includes(callState) || isMsTeamsToasterOffer) {
			setDetails({
				contactName: callSession?.caller?.name,
				contactInfo: callSession?.caller?.contactInformation,
				queueName: CallSessionHelper.resourceName(callSession),
				skills: callSession?.skills,
				offeringVariableText: callSession?.offeringVariableText,
				offeringVariableTextIsAiGenerated: callSession?.offeringVariableTextIsAiGenerated,
				transferInitiatorName: callSession?.transferInitiatorName,
				fallbackType: callSession?.fallbackType,
				fallbackFrom: callSession?.fallbackFrom,
				callbackQueueName: callSession?.callbackQueueName,
				isCallParked: callSession?.isCallParked,
			});
		} else {
			// Every state that is in no list above is one this card has nothing to say about, so clear it. Without
			// this branch a card already on screen simply stops being refreshed and stays up showing the caller,
			// number and queue of an interaction that has moved on — the deny-lists above only cover the states
			// reached today, and any state added later would strand it.
			setDetails(undefined);
		}
	}, [callSession]);

	return (
		<>
			{!!details && (
				<>
					<VoiceDetailsCard
						contactName={details.contactName}
						contactInfo={details.contactInfo}
						queueName={details.queueName}
						skills={details.skills}
						isNextToInternalToaster={false}
						offeringVariableText={details.offeringVariableText}
						offeringVariableTextIsAiGenerated={details.offeringVariableTextIsAiGenerated}
						transferInitiatorName={details.transferInitiatorName}
						fallbackType={details.fallbackType}
						fallbackFrom={details.fallbackFrom}
						callbackQueueName={details.callbackQueueName}
						isCallParked={details.isCallParked}
					/>
				</>
			)}
		</>
	);
};
